import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, Download, FileSpreadsheet, IndianRupee, RefreshCw } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Loading, Select, Stat, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { useSites } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { can } from '@/lib/domain'
import { ddmmyyyy, rupees, qty } from '@/lib/format'

const EXPORTS = [
  { id: 'stock_position', label: 'Current stock position', valued: true,
    note: 'Every material at every site, with below-minimum flags' },
  { id: 'inventory_ledger', label: 'Inventory movement ledger', note: 'Every stock movement with its source document' },
  { id: 'po_register', label: 'Purchase order register', note: 'All purchase orders with status, GST split and value' },
  { id: 'pr_register', label: 'Purchase requisition register', note: 'All requisitions with approval trail' },
  { id: 'grn_register', label: 'Goods receipt register', note: 'Receipts with acceptance and rejection split' },
  { id: 'vendor_payables', label: 'Vendor payables ageing', note: 'Outstanding payables by vendor and age' },
  { id: 'asset_register', label: 'Fixed asset register', note: 'Assets with book value and accumulated depreciation' },

  // The four Accounts posts into Tally each month. Restricted, because they
  // carry vendor pricing and the company's tax position.
  { id: 'vendor_invoices', label: 'Vendor invoice register', accounts: true,
    note: 'Every bill on file — matched, paid, and whether the ITC is claimable' },
  { id: 'credit_notes', label: 'Credit note register', accounts: true,
    note: 'What vendors have credited back, and why' },
  { id: 'payments_made', label: 'Payments made', accounts: true,
    note: 'Money out — advances and invoice settlements, with proof status' },
  { id: 'itc_register', label: 'Input tax credit register', accounts: true,
    note: 'ITC apportioned to each site and Project Manager, with both dates' },
  // Separate from the goods receipt register above, which is Stores' version.
  // This one carries vendor GSTINs, tax values and the Tally keying status —
  // it is the working file for the ad-hoc site purchases.
  { id: 'grn_accounts_register', label: 'Goods receipts for Tally', accounts: true,
    note: 'Every receipt with vendor GSTIN, tax split, paperwork count and what has been keyed' },

  // The board's liabilities pack. Also reachable from the Liabilities screen.
  { id: 'liabilities_company', label: 'Liabilities — company position', liability: true,
    note: 'One line: total owed, invoiced, not yet billed, overdue' },
  { id: 'liabilities_by_site', label: 'Liabilities — site wise', liability: true,
    note: 'Each site with its Project Manager and what is owed there' },
  { id: 'liabilities_by_pm', label: 'Liabilities — Project Manager wise', liability: true,
    note: 'Each manager\u2019s exposure across the sites he holds' },
  { id: 'liabilities_by_vendor', label: 'Liabilities — vendor wise', liability: true,
    note: 'Every vendor with a balance, with GSTIN and next due date' },
  { id: 'liabilities_detail', label: 'Liabilities — every open item', liability: true,
    note: 'One row per payable, with ageing. The one for the auditors' },
  { id: 'petty_cash_register', label: 'Petty cash purchase register', accounts: true,
    note: 'Cash spent at site — what it bought, from which shop, and whether the shop is named' },
]

/** Who may download the VALUED stock position. Mirrors the edge function.
 *  It carries a rupee figure for every site, so it is not for everyone —
 *  but showing a button that always errors is its own kind of bug. */
const VALUED_STOCK_ROLES = [
  'stores_head', 'accounts_spoc', 'cmd', 'cxo', 'project_head', 'admin',
]

/** Who may download the liabilities pack. Mirrors the edge function. */
const LIABILITY_EXPORT_ROLES = [
  'cmd', 'cxo', 'project_head', 'accounts_spoc',
  'stores_head', 'procurement_head', 'admin',
]

/** Who may download the four Accounts registers. Mirrors the edge function. */
const ACCOUNTS_EXPORT_ROLES = [
  'accounts_spoc', 'procurement_head', 'procurement_manager', 'stores_head',
  'project_head', 'cmd', 'cxo', 'admin',
]

const KINDS = [
  { value: 'WEEK', label: 'Week' },
  { value: 'MONTH', label: 'Month' },
  { value: 'QUARTER', label: 'Quarter' },
  { value: 'YEAR', label: 'Financial year' },
]

type Period = {
  period_key: string
  period_label: string
  week_from: string
  week_to: string
  week_count: number
}

export function Reports() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role

  const [kind, setKind] = useState('WEEK')
  const [periods, setPeriods] = useState<Period[]>([])
  const [periodKey, setPeriodKey] = useState('')
  const [site, setSite] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const { data: sites } = useSites()
  const { sort, toggle } = useSort('consumption_value', 'desc')

  // The list of reportable periods is worked out by the database, so the
  // Saturday rule and the labels live in exactly one place.
  useEffect(() => {
    let live = true
    supabase.rpc('rpc_consumption_periods', { p_kind: kind }).then(({ data, error }) => {
      if (!live) return
      if (error) { toast.error(error.message); return }
      const list = (data ?? []) as Period[]
      setPeriods(list)
      // Default to the most recent period that has actually closed, which is
      // the second entry — the first is the one still running.
      setPeriodKey(list[kind === 'WEEK' ? 1 : 0]?.period_key ?? list[0]?.period_key ?? '')
    })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])

  const period = useMemo(
    () => periods.find((p) => p.period_key === periodKey), [periods, periodKey])

  async function load() {
    if (!period) { setRows([]); return }
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('rpc_period_consumption', {
        p_week_from: period.week_from, p_week_to: period.week_to, p_site_id: site || null,
      })
      if (error) throw new Error(error.message)
      setRows(data ?? [])
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the consumption figures')
      setRows([])
    } finally { setLoading(false) }
  }

  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodKey, site, periods.length])

  const totals = useMemo(() => rows.reduce((a, r: any) => ({
    consumed: a.consumed + Number(r.consumption_qty || 0),
    received: a.received + Number(r.grn_in_qty || 0),
    value: a.value + Number(r.consumption_value || 0),
    norate: a.norate + (r.rate_source === 'No rate known' ? 1 : 0),
  }), { consumed: 0, received: 0, value: 0, norate: 0 }), [rows])

  const sorted = useMemo(() => sortRows(rows, sort, {
    opening_qty: 'number', grn_in_qty: 'number', mrn_in_qty: 'number', min_out_qty: 'number',
    ist_in_qty: 'number', ist_out_qty: 'number', adjustment_qty: 'number',
    closing_qty: 'number', consumption_qty: 'number',
    rate_per_purchase_uom: 'number', consumption_value: 'number',
  }), [rows, sort])

  async function rebuild() {
    if (!period) return
    setBusy('rebuild')
    try {
      // Every week in the period, so a month rebuild is a real month rebuild.
      let w = period.week_from
      while (w <= period.week_to) {
        const { error } = await supabase.rpc('rpc_generate_weekly_snapshot', {
          p_week_start: w, p_site_id: site || null, p_freeze: false,
        })
        if (error) throw new Error(error.message)
        w = new Date(new Date(w).getTime() + 7 * 864e5).toISOString().slice(0, 10)
      }
      toast.success(`Rebuilt ${period.week_count} week${period.week_count === 1 ? '' : 's'} from the ledger.`)
      load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not rebuild')
    } finally { setBusy(null) }
  }

  async function exportPeriod() {
    if (!period) return
    setBusy('period')
    try {
      await downloadFromFunction('weekly-consumption-xlsx', {
        week_from: period.week_from, week_to: period.week_to,
        period_label: period.period_label, period_kind: kind,
        site_id: site || null, regenerate: true,
      }, `OVAL_Consumption_${period.period_key}.xlsx`)
      toast.success('Consumption workbook downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed')
    } finally { setBusy(null) }
  }

  async function exportReport(id: string) {
    setBusy(id)
    try {
      await downloadFromFunction('export-report',
        { report: id, filters: site ? { site_id: site } : {} }, `${id}.xlsx`)
      toast.success('Workbook downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed')
    } finally { setBusy(null) }
  }

  return (
    <div className="space-y-4">
      <Card
        title="Material consumption"
        subtitle="What the sites burned, and what it cost — by week, month, quarter or financial year"
      >
        <Alert tone="info" title="How a period is built">
          The business week runs <strong>Saturday 00:00:01</strong> to <strong>Friday 23:59:59 IST</strong>,
          and Friday's closing stock becomes Saturday's opening stock. A month, quarter or year here is a
          whole number of those weeks — the weeks whose <strong>Saturday falls inside it</strong>. So a
          month is 4 or 5 weeks rather than the 1st to the 31st, and the figures tie back
          exactly to the weekly reports with no week split or counted twice.
          Consumption is <strong>issues to site less returns from site</strong>; inter-site transfers are
          excluded, because material moved to a sister site has not been consumed.
        </Alert>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Report for">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} options={KINDS} />
          </Field>
          <Field label="Period" required
                 hint={period ? `${ddmmyyyy(period.week_from)} to ${ddmmyyyy(period.week_to)}` : 'Loading…'}>
            <Select value={periodKey} onChange={(e) => setPeriodKey(e.target.value)}
                    options={periods.map((p) => ({ value: p.period_key, label: p.period_label }))} />
          </Field>
          <Field label="Site">
            <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites including HO"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <div className="flex items-end gap-2">
            {can(role, 'runWeekly') && (
              <button className="btn-secondary w-full" onClick={rebuild} disabled={busy === 'rebuild' || !period}>
                <RefreshCw className={`h-4 w-4 ${busy === 'rebuild' ? 'animate-spin' : ''}`} /> Rebuild
              </button>
            )}
          </div>
          <div className="flex items-end">
            <button className="btn-primary w-full" onClick={exportPeriod} disabled={busy === 'period' || !period}>
              <FileSpreadsheet className="h-4 w-4" /> Export to Excel
            </button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Material lines moved" value={rows.length} />
        <Stat label="Total received" value={qty(totals.received)} tone="good" />
        <Stat label="Total consumed" value={qty(totals.consumed)} tone="warn" />
        <Stat label="Consumption value" value={rupees(totals.value)}
              sub={period ? `${period.week_count} week${period.week_count === 1 ? '' : 's'}` : ''}
              icon={<IndianRupee className="h-5 w-5" />} />
      </div>

      {totals.norate > 0 && (
        <Alert tone="warning" title={`${totals.norate} line${totals.norate === 1 ? '' : 's'} could not be valued`}>
          Those materials have never been bought through a purchase order and carry no base rate on
          the material master, so they value at zero and the total above is understated.
          Set a base rate under <strong>Master Data → Materials</strong> to fix it.
        </Alert>
      )}

      <Card title="Consumption detail"
            subtitle="Opening + receipts − issues + returns ± adjustments = closing. Value is consumption × rate.">
        {loading && <Loading />}
        {!loading && rows.length === 0 && (
          <EmptyState
            title="Nothing moved in this period"
            message={can(role, 'runWeekly')
              ? 'Press Rebuild to recompute from the ledger, or choose a different period.'
              : 'Choose a different period.'}
          />
        )}
        {!loading && rows.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Material</Th>
                  <Th id="uom" sort={sort} onSort={toggle}>UOM</Th>
                  <Th id="opening_qty" sort={sort} onSort={toggle} className="num" align="right">Opening</Th>
                  <Th id="grn_in_qty" sort={sort} onSort={toggle} className="num" align="right">GRN in</Th>
                  <Th id="mrn_in_qty" sort={sort} onSort={toggle} className="num" align="right">MRN in</Th>
                  <Th id="min_out_qty" sort={sort} onSort={toggle} className="num" align="right">MIN out</Th>
                  <Th id="adjustment_qty" sort={sort} onSort={toggle} className="num" align="right">Adjust</Th>
                  <Th id="closing_qty" sort={sort} onSort={toggle} className="num" align="right">Closing</Th>
                  <Th id="consumption_qty" sort={sort} onSort={toggle} className="num" align="right">Consumed</Th>
                  <Th id="rate_per_purchase_uom" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="rate_source" sort={sort} onSort={toggle}>Rate source</Th>
                  <Th id="consumption_value" sort={sort} onSort={toggle} className="num" align="right">Value</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => (
                  <tr key={`${r.site_id}-${r.material_id}`}>
                    <td className="text-xs">{r.site_name}</td>
                    <td className="text-sm font-medium">{r.item_name}</td>
                    <td className="text-xs text-slate-500">{r.uom}</td>
                    <td className="num text-xs">{qty(r.opening_qty)}</td>
                    <td className="num text-xs text-emerald-700">{qty(r.grn_in_qty)}</td>
                    <td className="num text-xs text-emerald-700">{qty(r.mrn_in_qty)}</td>
                    <td className="num text-xs text-amber-700">{qty(r.min_out_qty)}</td>
                    <td className="num text-xs text-violet-700">{qty(r.adjustment_qty)}</td>
                    <td className="num text-sm font-semibold">{qty(r.closing_qty)}</td>
                    <td className="num text-sm font-bold text-slate-900">{qty(r.consumption_qty)}</td>
                    <td className="num text-xs">{rupees(r.rate_per_purchase_uom)}</td>
                    <td className={`text-xs ${r.rate_source === 'No rate known' ? 'text-red-600' : 'text-slate-500'}`}>
                      {r.rate_source}
                    </td>
                    <td className="num text-sm font-bold text-slate-900">{rupees(r.consumption_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Other registers" subtitle="Each downloads as a formatted Excel workbook with a cover sheet">
        <div className="grid gap-2 sm:grid-cols-2">
          {EXPORTS
            // A button that returns "403 ask Accounts" is worse than no button.
            .filter((r) => !r.accounts || ACCOUNTS_EXPORT_ROLES.includes(role))
            .filter((r) => !r.liability || LIABILITY_EXPORT_ROLES.includes(role))
            .filter((r) => !r.valued || VALUED_STOCK_ROLES.includes(role))
            .map((r) => (
            <button
              key={r.id}
              onClick={() => exportReport(r.id)}
              disabled={busy === r.id}
              className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:border-oval-300 hover:bg-oval-50 disabled:opacity-60"
            >
              <Download className="mt-0.5 h-4 w-4 shrink-0 text-oval-600" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-800">{r.label}</span>
                <span className="block text-xs text-slate-500">{r.note}</span>
              </span>
            </button>
          ))}
        </div>
      </Card>

      <p className="px-1 text-xs text-slate-400">
        <CalendarDays className="mr-1 inline h-3 w-3" />
        Values use today's best-known rate — the last purchase rate for the site, then anywhere,
        then the material's base rate. It is a management figure, not a stock-ledger valuation.
      </p>
    </div>
  )
}
